"use client";

import { useCallback } from "react";
import type {
  AssignSituationStrategy,
  ConfirmThoughtsRequest,
  DeleteThoughtsRequest,
  MeiliSuggestedThought,
  MindstringPrototype,
} from "@/generated/fastapi";
import { useMeiliRuntime } from "@/core/search";
import {
  showErrorToast,
  showSuccessToast,
  showWarningToast,
} from "@/shared/feedback";
import type { ConfirmationDialogController } from "@/shared/ui/useConfirmationDialog";
import useConfirmThoughtsMutation from "../mutations/useConfirmThoughtsMutation";
import useDeleteThoughtsMutation from "../mutations/useDeleteThoughtsMutation";
import useThoughtRefresh from "./useThoughtRefresh";

export default function useSuggestedThoughtActions({
  confirmSuggestedThoughtConfirmation,
  setConfirmSuggestedThoughtPrototypes,
  validSituationIds,
}: {
  confirmSuggestedThoughtConfirmation?: ConfirmationDialogController<{
    situationId?: string;
    assignStrategy?: AssignSituationStrategy;
  }>;
  setConfirmSuggestedThoughtPrototypes?: (
    prototypes: MindstringPrototype[],
  ) => void;
  validSituationIds?: string[];
} = {}) {
  const { searchThoughtDocuments } = useMeiliRuntime();
  const deleteThoughtsMutation = useDeleteThoughtsMutation();
  const confirmThoughtsMutation = useConfirmThoughtsMutation();
  const {
    refreshReflectionSurfaces,
    syncDeletedThoughts,
    syncSuggestedThoughtConfirmation,
  } = useThoughtRefresh();

  const handleSuggestedThoughtsDeleteClick = useCallback(
    async (
      request: DeleteThoughtsRequest,
      onSuccess?: () => void,
      affectedPrototypes?: MindstringPrototype[],
    ) => {
      try {
        const response = await deleteThoughtsMutation.mutateAsync(request);
        if (response.status !== "success" && response.status !== "pending") {
          showErrorToast(
            response.message || "Failed to delete suggested thoughts",
          );
          return;
        }

        syncDeletedThoughts({
          response,
          indexName: request.sync_meili_index,
          prototypes: affectedPrototypes,
        });
        onSuccess?.();

        if (response.status === "pending") {
          showWarningToast(
            response.message ||
              "Suggested thoughts removed, but search is still catching up.",
          );
        } else {
          showSuccessToast("Suggested thoughts deleted successfully");
        }
      } catch {
        showErrorToast("Failed to delete suggested thoughts");
      }
    },
    [deleteThoughtsMutation, syncDeletedThoughts],
  );

  const handleSuggestedThoughtsConfirmClick = useCallback(
    async ({
      request,
      onSuccess,
    }: {
      request: ConfirmThoughtsRequest;
      onSuccess?: () => void;
    }) => {
      const searchResults = await searchThoughtDocuments<MeiliSuggestedThought>({
        indexName: "suggested_thoughts",
        items_ids: request.thought_ids,
      });

      const affectedPrototypes = Array.from(
        new Set(
          searchResults.map(
            (thought) => thought.prototype as MindstringPrototype,
          ),
        ),
      );
      const validSituationIdSet = validSituationIds
        ? new Set(validSituationIds)
        : null;
      const isAnyNeedingAnchor = searchResults.some(
        (thought) =>
          !thought.situation_id ||
          (validSituationIdSet !== null &&
            !validSituationIdSet.has(thought.situation_id)),
      );

      if (isAnyNeedingAnchor && !confirmSuggestedThoughtConfirmation) {
        throw new Error(
          "Thought is not anchored to a situation; provide confirmation dialog to proceed.",
        );
      }

      const executeConfirm = async (dynamicValues?: {
        situationId?: string;
        assignStrategy?: AssignSituationStrategy;
      }) => {
        const finalRequest: ConfirmThoughtsRequest = {
          ...request,
          assign_situation_id:
            dynamicValues?.situationId || request.assign_situation_id,
          assign_situation_strategy:
            dynamicValues?.assignStrategy || request.assign_situation_strategy,
        };

        const confirmationResponse =
          await confirmThoughtsMutation.mutateAsync(finalRequest);

        if (
          confirmationResponse &&
          (confirmationResponse.status === "success" ||
            confirmationResponse.status === "pending")
        ) {
          onSuccess?.();

          if (finalRequest.reset_reflections) {
            refreshReflectionSurfaces();
          }

          syncSuggestedThoughtConfirmation(
            confirmationResponse,
            affectedPrototypes,
          );

          if (confirmationResponse.status === "pending") {
            showWarningToast(
              confirmationResponse.message ||
                "Thoughts updated, but search is still catching up.",
            );
          } else {
            showSuccessToast("Thoughts confirmed in your profile");
          }
          return;
        }

        if (
          confirmationResponse &&
          confirmationResponse.status === "not_found"
        ) {
          showErrorToast(`Not successfull: ${confirmationResponse.message}`);
          return;
        }

        if (
          confirmationResponse &&
          confirmationResponse.status === "validation_error"
        ) {
          showErrorToast(
            confirmationResponse.message || "Failed to confirm suggested thoughts",
          );
          return;
        }

        showErrorToast("Failed to confirm suggested thoughts");
      };

      if (isAnyNeedingAnchor && confirmSuggestedThoughtConfirmation) {
        setConfirmSuggestedThoughtPrototypes?.(affectedPrototypes);
        confirmSuggestedThoughtConfirmation.onOpen((dynamicValues) =>
          executeConfirm(dynamicValues),
        );
        return;
      }

      await executeConfirm();
    },
    [
      confirmSuggestedThoughtConfirmation,
      confirmThoughtsMutation,
      refreshReflectionSurfaces,
      searchThoughtDocuments,
      setConfirmSuggestedThoughtPrototypes,
      syncSuggestedThoughtConfirmation,
      validSituationIds,
    ],
  );

  return {
    handleSuggestedThoughtsDeleteClick,
    handleSuggestedThoughtsConfirmClick,
  };
}
